import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useGuidedTour } from "@/lib/guidedTour";

// Cue start = where the phrase sits in the caption, as a fraction of the clip.
const cueStarts = (caption, cues) => cues.map((c) => {
  const i = caption.toLowerCase().indexOf(c.phrase.toLowerCase());
  return i < 0 ? 0 : i / Math.max(1, caption.length);
});

// Index of the phrase the narration is currently on (-1 before the first), for the active step.
export function useNarrationCue(phrases, active) {
  const { current, getAudioProgress, step, pending } = useGuidedTour();
  const [idx, setIdx] = useState(-1);
  const key = phrases.join("|");
  useEffect(() => {
    setIdx(-1);
    if (!active || pending) return undefined;
    const caption = current?.caption || "";
    const starts = cueStarts(caption, phrases.map((phrase) => ({ phrase })));
    const t0 = performance.now();
    const est = Math.max(4000, (caption.split(/\s+/).length / 2.5) * 1000);
    const id = setInterval(() => {
      const p = getAudioProgress() ?? Math.min(1, (performance.now() - t0) / est);
      let i = -1;
      starts.forEach((s, j) => { if (p >= s) i = j; });
      setIdx(i);
    }, 150);
    return () => clearInterval(id);
  }, [active, key, current, step, getAudioProgress, pending]); // eslint-disable-line react-hooks/exhaustive-deps
  return idx;
}

// Moves a highlight + bouncing arrow onto whatever the narration is talking about right now.
export default function TourCuePointer({ color = "#D4AF37" }) {
  const { current, getAudioProgress, step, pending } = useGuidedTour();
  const cues = current?.cues;
  const [rect, setRect] = useState(null);

  useEffect(() => {
    setRect(null);
    if (!cues?.length || pending) return undefined;
    const starts = cueStarts(current.caption || "", cues);
    const t0 = performance.now();
    const est = Math.max(4000, ((current.caption || "").split(/\s+/).length / 2.5) * 1000);
    let lastIdx = -1;
    const tick = () => {
      const p = getAudioProgress() ?? Math.min(1, (performance.now() - t0) / est);
      let idx = -1;
      starts.forEach((s, i) => { if (p >= s) idx = i; });
      const el = idx >= 0 ? document.querySelector(`[data-testid="${cues[idx].target}"]`) : null;
      const r = el?.getBoundingClientRect();
      if (el && idx !== lastIdx) {
        lastIdx = idx;
        const strip = document.querySelector('[data-testid="veroogle-tour-strip"]')?.getBoundingClientRect().bottom || 0;
        if (r.top < strip + 50 || r.bottom > window.innerHeight - 10) {
          window.scrollBy({ top: r.top - strip - 60, behavior: "smooth" });
        }
      }
      setRect(r ? { top: r.top, left: r.left, width: r.width, height: r.height, key: idx } : null);
    };
    tick();
    const id = setInterval(tick, 150);
    window.addEventListener("scroll", tick, true);
    return () => { clearInterval(id); window.removeEventListener("scroll", tick, true); };
  }, [cues, current, getAudioProgress, step, pending]);

  if (!rect) return null;
  return (
    <div className="fixed inset-0 z-[93] pointer-events-none" data-testid="tour-cue-pointer" data-cue-index={rect.key}>
      <motion.div
        className="absolute rounded-2xl"
        style={{ boxShadow: `0 0 0 2px ${color}, 0 0 30px 4px ${color}66` }}
        animate={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
        transition={{ type: "spring", stiffness: 220, damping: 26 }}
      />
      <motion.div
        className="absolute"
        animate={{ top: rect.top - 44, left: rect.left + rect.width / 2 - 14 }}
        transition={{ type: "spring", stiffness: 220, damping: 26 }}
      >
        <motion.svg width="28" height="34" viewBox="0 0 28 34" animate={{ y: [0, 8, 0] }} transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
          style={{ filter: `drop-shadow(0 0 8px ${color})` }}>
          <path d="M14 34 L28 18 L19 18 L19 0 L9 0 L9 18 L0 18 Z" fill={color} />
        </motion.svg>
      </motion.div>
    </div>
  );
}
